import React, {ReactElement, ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import ReactDOM from "react-dom";

import {MenuAlignment, MenuPlacement, normalizePosition} from "../../../utils/normalizePosition";
import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";
import {ModalBase} from "../../screens/modals/components/ModalBase";

import './HoverCard.css';

let _hoverCardRootHTML: HTMLElement | null = null;
const getHoverCardRootHTML = (): HTMLElement => {
    if (!_hoverCardRootHTML) _hoverCardRootHTML = window.document.getElementById('tooltip')!;
    return _hoverCardRootHTML;
};

export interface HoverCardProps {
    content: ReactNode;
    children: ReactElement;
    menuPlacement?: MenuPlacement;
    menuAlignment?: MenuAlignment;
    VERTICAL_SHIFT?: number;
    HORIZONTAL_SHIFT?: number;
}

// A hover-triggered popover for rich (non-text) content — e.g. a list of rows
// with icons and links. Distinct from Tooltip, which always renders a small
// dark bubble meant for short text labels.
//
// Hover only works with a mouse, so touch breakpoints swap the trigger to tap:
// tablet reuses the same anchored portal card (just opened/closed by click
// instead of hover, since there's no hover state to react to), and mobile
// swaps the anchored card for a ModalBase bottom sheet — an anchored popover
// has nowhere good to sit above a thumb-width tap target on a narrow screen.
export const HoverCard = (
    {
        content,
        children,
        menuPlacement = "block-end",
        menuAlignment = "start",
        VERTICAL_SHIFT = 4,
        HORIZONTAL_SHIFT = 0,
    }: HoverCardProps
) => {
    const [isOpen, setIsOpen] = useState(false);
    const [style, setStyle] = useState<{top: number; left: number} | undefined>(undefined);
    const cardRef = useRef<HTMLDivElement>(null);
    const childRef = useRef<HTMLElement>(null);
    const enterTimeoutRef = useRef<number | undefined>(undefined);
    const leaveTimeoutRef = useRef<number | undefined>(undefined);
    const view = screenSizeProvider.view;
    const isTouch = view !== 'desktop';
    const isMobile = view === 'mobile';

    const open = () => {
        enterTimeoutRef.current = window.setTimeout(() => setIsOpen(true), 300);
        if (leaveTimeoutRef.current !== undefined) clearTimeout(leaveTimeoutRef.current);
    };

    const close = () => {
        if (enterTimeoutRef.current !== undefined) clearTimeout(enterTimeoutRef.current);
        leaveTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 300);
    };

    const cancelClose = () => {
        if (leaveTimeoutRef.current !== undefined) clearTimeout(leaveTimeoutRef.current);
    };

    const closeNow = () => setIsOpen(false);
    const toggleOpen = () => setIsOpen((prev) => !prev);

    useLayoutEffect(() => {
        if (isMobile || !isOpen || !cardRef.current || !childRef.current) return;
        const targetBoundingBox = childRef.current.getBoundingClientRect();
        const rootHTML = getHoverCardRootHTML();
        const result = normalizePosition({
            contextMenu: cardRef.current,
            placement: menuPlacement,
            alignment: menuAlignment,
            targetBoundingBox,
            VERTICAL_SHIFT: VERTICAL_SHIFT,
            HORIZONTAL_SHIFT: HORIZONTAL_SHIFT,
            getScopeBoundingClientRect: rootHTML.getBoundingClientRect.bind(rootHTML),
        });
        setStyle({top: result.top, left: result.left});
    }, [isOpen, content, menuPlacement, menuAlignment, isMobile]);

    // Tablet's popover has no hover to leave, so it closes on an outside
    // tap/click or Escape instead - mirrors PaddleLikeSelect's own listbox.
    useEffect(() => {
        if (!isTouch || isMobile || !isOpen) return;
        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;
            if (cardRef.current?.contains(target)) return;
            if (childRef.current?.contains(target)) return;
            closeNow();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeNow();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, isTouch, isMobile]);

    if (isMobile) {
        return (
            <>
                {React.cloneElement(children, {
                    ref: childRef,
                    onClick: (event: React.MouseEvent) => {
                        children.props.onClick?.(event);
                        toggleOpen();
                    },
                })}
                <ModalBase open={isOpen} onClose={closeNow} isMobileBottomSheet>
                    {content}
                </ModalBase>
            </>
        );
    }

    if (isTouch) {
        return (
            <>
                {React.cloneElement(children, {
                    ref: childRef,
                    onClick: (event: React.MouseEvent) => {
                        children.props.onClick?.(event);
                        toggleOpen();
                    },
                })}
                {isOpen && ReactDOM.createPortal(
                    <div
                        ref={cardRef}
                        className="HoverCard"
                        style={style}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {content}
                    </div>,
                    getHoverCardRootHTML()
                )}
            </>
        );
    }

    return (
        <>
            {React.cloneElement(children, {
                ref: childRef,
                onMouseEnter: open,
                onMouseLeave: close,
            })}
            {isOpen && ReactDOM.createPortal(
                <div
                    ref={cardRef}
                    className="HoverCard"
                    style={style}
                    onMouseEnter={cancelClose}
                    onMouseLeave={close}
                    onClick={(e) => e.stopPropagation()}
                >
                    {content}
                </div>,
                getHoverCardRootHTML()
            )}
        </>
    );
};

export default HoverCard;
